<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ t('academic.add_year') }}</h6>
          </div>
        </div>
      </div>
      
      <div class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-md-8 ms-3">
            <form @submit.prevent="submitForm" @keydown.enter.prevent>
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('academic.year_name') }}</label>
                  <input 
                    type="text" 
                    v-model="form.name" 
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.name }"
                    required
                    @blur="validateField('name')"
                  >
                  <div class="invalid-feedback">{{ errors.name }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('academic.year_code') }}</label>
                  <input 
                    type="text" 
                    v-model="form.year" 
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.year }"
                    required
                    @blur="validateField('year')"
                  >
                  <div class="invalid-feedback">{{ errors.year }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('common.start_date') }}</label>
                  <input 
                    type="date" 
                    v-model="form.start_date" 
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.start_date }"
                    required
                    @blur="validateField('start_date')"
                  >
                  <div class="invalid-feedback">{{ errors.start_date }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('common.end_date') }}</label>
                  <input 
                    type="date" 
                    v-model="form.end_date" 
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.end_date }"
                    required
                    @blur="validateField('end_date')"
                  >
                  <div class="invalid-feedback">{{ errors.end_date }}</div>
                </div>
                
                <div class="col-12">
                  <div class="form-check">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      v-model="form.is_active"
                      id="is_active"
                    >
                    <label class="form-check-label" for="is_active">
                      {{ t('academic.set_as_active') }}
                    </label>
                  </div>
                </div>
                
                <div class="col-12 mt-4">
                  <div class="d-flex gap-3">
                    <button 
                      type="submit" 
                      class="btn btn-primary flex-grow-1"
                      :disabled="loading"
                    >
                      <span v-if="loading">
                        <i class="fas fa-spinner fa-spin me-2"></i>
                        {{ t('academic.saving') }}
                      </span>
                      <span v-else>
                        <i class="fas fa-save ms-2"></i>
                        {{ t('academic.add_year') }}
                      </span>
                    </button>
                    <router-link 
                      :to="{ name: 'academic.years' }" 
                      class="btn btn-outline-secondary flex-grow-1"
                    >
                      <i class="fas fa-times ms-2"></i>
                      {{ t('common.cancel') }}
                    </router-link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';

const { t } = useI18n();
const toast = useToast();
const router = useRouter();
const loading = ref(false);

const form = ref({
  name: '',
  year: '',
  start_date: '',
  end_date: '',
  is_active: false
});

const errors = ref({});

const validateField = (field) => {
  if (!form.value[field]) {
    errors.value[field] = t('validation.required');
    return false;
  }
  delete errors.value[field];
  return true;
};

const validateForm = () => {
  let isValid = true;
  const requiredFields = ['name', 'year', 'start_date', 'end_date'];
  
  requiredFields.forEach(field => {
    if (!validateField(field)) {
      isValid = false;
    }
  });
  
  // Validate date range
  if (form.value.start_date && form.value.end_date) {
    const startDate = new Date(form.value.start_date);
    const endDate = new Date(form.value.end_date);
    if (endDate <= startDate) {
      errors.value.end_date = t('validation.end_date_after_start');
      isValid = false;
    }
  }
  
  return isValid;
};

const submitForm = async () => {
  if (!validateForm()) {
    toast.warning(t('validation.please_correct_errors'));
    return;
  }

  loading.value = true;
  try {
    await api.post('/academic-years', form.value);
    toast.success(t('academic.year_created'));
    router.push({ name: 'academic.years' });
  } catch (error) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors;
      Object.values(error.response.data.errors).forEach(errors => {
        errors.forEach(message => toast.error(message));
      });
    } else {
      toast.error(error.response?.data?.message || t('errors.operation_failed'));
    }
  } finally {
    loading.value = false;
  }
};
</script>